import type { FC } from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as zod from 'zod';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import type { InputCurrencyProps } from '@packages/shared/src/ui/Input/InputCurrency';
import { InputCurrency } from '@packages/shared/src/ui/Input/InputCurrency';

const messages = defineMessages({
  textFieldLabel: {
    id: 'CurrencyInput.textFieldLabel',
    defaultMessage: 'Wunschbetrag in Euro',
  },
  formatError: {
    id: 'CurrencyInput.formatError',
    defaultMessage:
      'Nur folgende Eingaben sind gültig: Zahlen, ein Komma, höchstens zwei Nachkommastellen.',
  },
  minAmountError: {
    id: 'CurrencyInput.minAmountError',
    defaultMessage:
      'Dein Wunschbetrag darf nicht kleiner als die angegebene Fälligkeit ({minimum}) sein.',
  },
  maxAmountError: {
    id: 'CurrencyInput.maxAmountError',
    defaultMessage: 'Du hast den Maximalbetrag von {maxValue} überschritten.',
  },
});

interface CurrencyInputValues {
  currency: string;
}

export const CurrencyInput: FC<{
  overrideLabel?: MessageDescriptor;
  initialAmount: number | null;
  minAmount: number | null;
  maxAmount: number | null;
  currencyCode: string | null;
  onChange: (value: number) => void;
}> = ({ initialAmount, minAmount, maxAmount, onChange, currencyCode, overrideLabel }) => {
  const { formatMessage, locale } = useIntl();
  const format = useFormatPrice();
  const currency = currencyCode || 'EUR';
  const schema = useMemo(
    () =>
      zod.object({
        currency: zod
          .string()
          // allows white space before and after only focuses on the number
          .regex(/^\s*\d+(,\d{2})?\s*?$/, formatMessage(messages.formatError)) // only allow numbers with up to 2 decimal places no currency symbol
          .refine(
            (value) => {
              if (minAmount) {
                const number = parseFloat(value.replace(',', '.')) * 100;
                return minAmount <= number;
              }
              return true;
            },
            {
              message: formatMessage(messages.minAmountError, {
                minimum: format.formatPrice(minAmount || 0, currency),
              }),
            },
          )
          .refine(
            (value) => {
              if (maxAmount) {
                const number = parseFloat(value.replace(',', '.')) * 100;
                return number <= maxAmount;
              }
              return true;
            },
            {
              message: formatMessage(messages.maxAmountError, {
                maxValue: format.formatPrice(maxAmount || 0, currency),
              }),
            },
          ),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [format, formatMessage, minAmount, maxAmount],
  );

  const {
    register,
    formState: { errors },
  } = useForm<CurrencyInputValues>({
    defaultValues: {
      // why no currency given as object parameter, because it would be formatted as "€ 9,99" or "9,99 €" this prevents the regex from working we need to format it as "9,99"
      currency: initialAmount ? (initialAmount / 100).toLocaleString(locale) : '0,00',
    },
    resolver: zodResolver(schema),
    mode: 'onChange',
  });
  return (
    <InputCurrency
      containerClassName="mt-2"
      currency={currency as InputCurrencyProps['currency']}
      currencyPosition="left"
      {...register('currency')}
      label={formatMessage(overrideLabel ?? messages.textFieldLabel)}
      error={!!errors.currency}
      helperText={errors.currency?.message}
      onChangeCapture={(e) => {
        // used onChangeCapture because onChange kills the inline validation
        onChange(Math.round(parseFloat(e.currentTarget.value.replace(',', '.')) * 100));
      }}
    />
  );
};
